import {Card, openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {head, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {DmsDataGrid, Section, catchUnhandledDataGridActions, logError} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, EagGridMaximization, QueryFilterObject} from 'features/eag-grid';

import {SupplierArticlesAddNew} from './components/SupplierArticlesAddNew';

const PREDEFINED_COLUMNS = ['supplierArticleId'] as const;

interface SupplierArticlesProps extends RequiredTestIdProps {
  maximization?: EagGridMaximization;
  authorizationProfileId: string;
}

export function SupplierArticles(props: SupplierArticlesProps) {
  const {warehouseId, articleId} = useWarehouseParams();

  // TODO We don't have POST yet, keep commented out
  // const handleNew = () => {
  //   openDialog(<SupplierArticlesAddNew data-testid={suffixTestId('addNewDialog', props)} />, {
  //     id: 'addNewDialog',
  //     title: i18n.t('general.actions.addNew'),
  //     size: 'large',
  //     scrollBehavior: 'outside',
  //     withAdditionalFooter: true,
  //     'data-testid': suffixTestId('dialogs.addNew', props),
  //   });
  // };

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(id)) {
      return logError('Supplier article id is not defined');
    }

    match(actionKey)
      .with('edit', () => {
        const supplierArticleId = getColumnData<string>('supplierArticleId')?.value;

        if (isNil(supplierArticleId)) {
          return logError('supplierArticleId is not defined');
        }

        openDialog(
          <SupplierArticlesAddNew
            isEditing
            warehouseId={warehouseId}
            articleId={articleId}
            authorizationProfileId={props.authorizationProfileId}
            supplierArticleId={supplierArticleId}
            onAfterSubmit={refreshData}
            data-testid={suffixTestId('addNewDialog', props)}
          />,
          {
            id: 'editingDialog',
            title: i18n.t('entity.warehouse.labels.editSupplierArticle'),
            size: 'large',
            scrollBehavior: 'inside',
            withAdditionalFooter: true,
            'data-testid': suffixTestId('dialogs.edit', props),
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {articleId}]),
    [articleId]
  );

  return (
    <Section isFullHeight>
      <Card
        title={i18n.t('entity.warehouse.labels.supplierArticles')}
        // TODO We don't have POST yet, keep commented out
        // actions={[
        //   {
        //     type: 'button',
        //     variant: 'link',
        //     leftIcon: 'content/add_circle',
        //     title: i18n.t('general.actions.addNew'),
        //     onClick: handleNew,
        //     'data-testid': suffixTestId('actions.addNew', props),
        //   },
        // ]}
        isFullHeight
      >
        <div css={{height: '100%'}}>
          <DmsDataGrid
            maximization={props.maximization}
            gridCode="warehouse-supplier-article"
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
            data-testid={suffixTestId('supplierArticles', props)}
          />
        </div>
      </Card>
    </Section>
  );
}
